import { Button } from "@/components/ui/button";
import { titleAuthConfigured } from "@/lib/title-auth";
import { claimTitleRequest } from "@/lib/title-request-api";
import { useAuth0 } from "@auth0/auth0-react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

function ClaimResult({ token }: { token: string; bearer?: string | null }) {
  const [message, setMessage] = useState("Confirming your request…");
  const [requestId, setRequestId] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setMessage("This confirmation link is missing a token.");
      return;
    }
    let cancelled = false;
    void claimTitleRequest(token)
      .then((detail) => {
        if (cancelled) return;
        setRequestId(detail.id);
        setMessage(
          detail.claimed
            ? "This request is saved to your account."
            : "Your email is confirmed. Log in with the same email to see this request.",
        );
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setMessage(
          error instanceof Error
            ? error.message
            : "This link could not be used.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-3">
      <h1 className="text-2xl font-semibold">Title request</h1>
      <p>{message}</p>
      {requestId ? (
        <Button variant="outline" asChild className="w-fit">
          <Link to="/title/requests">My requests</Link>
        </Button>
      ) : null}
    </div>
  );
}

function AuthedClaim({ token }: { token: string }) {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [message, setMessage] = useState("Confirming your request…");

  useEffect(() => {
    if (!token) {
      setMessage("This confirmation link is missing a token.");
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const bearer = isAuthenticated ? await getAccessTokenSilently() : null;
        const detail = await claimTitleRequest(token, bearer);
        if (cancelled) return;
        setMessage(
          detail.claimed
            ? "This request is saved to your account."
            : "Your email is confirmed. Log in with the same email to see this request.",
        );
      } catch (error) {
        if (cancelled) return;
        setMessage(
          error instanceof Error
            ? error.message
            : "This link could not be used.",
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, isAuthenticated, token]);

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-3">
      <h1 className="text-2xl font-semibold">Title request</h1>
      <p>{message}</p>
      <Button variant="outline" asChild className="w-fit">
        <Link to="/title/requests">My requests</Link>
      </Button>
    </div>
  );
}

export const Route = createFileRoute("/title/claim")({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  component: function ClaimPage() {
    const { token } = Route.useSearch();
    if (!titleAuthConfigured()) {
      return <ClaimResult token={token} />;
    }
    return <AuthedClaim token={token} />;
  },
});
